import { usePrivy } from '@privy-io/react-auth';
import { Check, ListX, Plus, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import z from 'zod';

import { Button, Input, Toggle } from '@/components/ui';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { buttonVariants } from '@/components/ui/button';
import { Form, FormControl, FormField } from '@/components/ui/form';
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from '@/components/ui/input-otp';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { usePrivyAccount } from '@/hooks';
import { useAccountStore } from '@/store/use-account-store';
import {
  ErrorCode,
  NotificationChannelStatus,
  NotificationChannelType,
} from '@/types';
import { cn } from '@/utils/cn';

import useCreateNotificationChannel, {
  MANDATORY_NOTIFICATION_TYPES,
} from '../../api/notifications/create-channel';
import useDeleteChannel from '../../api/notifications/delete-channel';
import useNotifications, {
  NotificationChannel,
} from '../../api/notifications/get-notifications-settings';
import useSendChannelCode from '../../api/notifications/send-channel-code';
import useToggleNotifications from '../../api/notifications/toggle-notification';
import useVerifyChannel from '../../api/notifications/verify-channel';

const formSchema = z.object({
  type: z.nativeEnum(NotificationChannelType),
  destination: z.string().min(1),
});

type FormSchema = z.infer<typeof formSchema>;

export default function Notifications() {
  const [isFormVisible, setIsFormVisible] = useState(false);
  const {
    data: notifications,
    isLoading: areNotificationsLoading,
    isRefetching: areNotificationsRefetching,
    isError,
  } = useNotifications();
  const { t } = useTranslation();

  const hasAuth = useAccountStore((state) => state.hasAuth);

  const hasAllChannels = useMemo(() => {
    return Object.values(NotificationChannelType).every((channelType) =>
      notifications?.channels.some((channel) => channel.type === channelType),
    );
  }, [notifications?.channels]);

  const { ready, authenticated } = usePrivy();
  const isLoading =
    areNotificationsLoading || !ready || (authenticated && !hasAuth);

  // const { mutate: toggle, isPending } = useToggleNotifications();
  return (
    <ScrollArea className="h-full px-6 sm:px-10">
      <div className="space-y-8 pb-32 sm:pb-10">
        <div className="space-y-4">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
            <div className="space-y-1">
              <span>{t('settingsDropdown.channels')}</span>
              <p className="text-muted-foreground">
                {t('settingsDropdown.channelsDescription')}
              </p>
            </div>
            <Button
              variant="outline2"
              className="bg-muted hover:border-border dark:bg-background"
              onClick={() => setIsFormVisible(true)}
              disabled={
                !hasAuth ||
                isFormVisible ||
                isLoading ||
                areNotificationsRefetching ||
                isError ||
                hasAllChannels
              }
            >
              <Plus size={16} />
              {t('settingsDropdown.createChannel')}
            </Button>
          </div>
          <div className="space-y-3">
            {isFormVisible && (
              <ChannelForm onHide={() => setIsFormVisible(false)} />
            )}
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="flex flex-col items-center gap-4">
                  <div className="relative">
                    <div className="h-8 w-8 animate-spin rounded-full border-2 border-border border-t-primary"></div>
                    <div className="absolute inset-0 h-8 w-8 animate-ping rounded-full border border-primary opacity-20"></div>
                  </div>
                  <p className="font-mono text-sm text-muted-foreground">
                    {t('settingsDropdown.loadingChannels')}
                  </p>
                </div>
              </div>
            ) : notifications && notifications.channels.length > 0 ? (
              <Accordion type="single" className="space-y-3" collapsible>
                {notifications.channels.map((field) => (
                  <ChannelRef {...field} key={field.channelId} />
                ))}
              </Accordion>
            ) : (
              !isFormVisible && (
                <div className="flex flex-col items-center justify-center gap-1 pt-4">
                  <ListX size={20} />
                  <span className="text-center font-medium capitalize">
                    {t('settingsDropdown.noChannels')}
                  </span>
                  <span className="text-center text-sm text-muted-foreground">
                    {t('settingsDropdown.noChannelsDescription')}
                  </span>
                </div>
              )
            )}
          </div>
        </div>
      </div>
    </ScrollArea>
  );
}

const getChannelStatusLabel = (status: NotificationChannelStatus) => {
  switch (status) {
    case NotificationChannelStatus.ENABLED:
      return 'Active';
    case NotificationChannelStatus.UNVERIFIED:
      return 'Unverified';
  }
};

const ChannelRef = ({
  channelId,
  preferences,
  destination,
  status,
  type,
}: NotificationChannel) => {
  const { t } = useTranslation();
  const { data: notifications } = useNotifications();

  const notificationTypes = useMemo(() => {
    if (!notifications?.availableNotificationTypes) return [];
    return notifications.availableNotificationTypes.sort((a, b) => {
      if (
        MANDATORY_NOTIFICATION_TYPES.includes(a) &&
        !MANDATORY_NOTIFICATION_TYPES.includes(b)
      )
        return -1;
      if (
        !MANDATORY_NOTIFICATION_TYPES.includes(a) &&
        MANDATORY_NOTIFICATION_TYPES.includes(b)
      )
        return 1;
      return 0;
    });
  }, [notifications?.availableNotificationTypes]);

  const { mutate: deleteChannel, isPending: isDeleting } = useDeleteChannel();
  const {
    isSuccess: isSent,
    mutate: sendChannelCode,
    isPending: isSendingCode,
    delayInSecs,
  } = useSendChannelCode();

  const [isCodeFormVisible, setIsCodeFormVisible] = useState(false);

  useEffect(() => {
    setIsCodeFormVisible(
      isSent && status === NotificationChannelStatus.UNVERIFIED,
    );
  }, [isSent, status]);

  return (
    <AccordionItem className="border-b-0" value={channelId}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span
          className={cn(
            status === NotificationChannelStatus.ENABLED
              ? 'text-green'
              : 'text-red',
          )}
        >
          {getChannelStatusLabel(status)}
        </span>
        <span>{t('settingsDropdown.email')}</span>
        <span>{destination}</span>
        <div className="flex items-center gap-3">
          {status === NotificationChannelStatus.UNVERIFIED && (
            <Button
              className="h-10 bg-muted text-sm hover:border-border dark:bg-background"
              variant="outline2"
              type="button"
              disabled={isDeleting || isSendingCode || delayInSecs > 0}
              onClick={() => sendChannelCode({ channelId })}
            >
              {delayInSecs > 0 ? `Resend in ${delayInSecs}s` : 'Verify'}
            </Button>
          )}
          <AccordionTrigger
            className={cn(
              buttonVariants({ variant: 'outline2' }),
              'h-10 bg-muted text-sm hover:border-border dark:bg-background',
            )}
          >
            {t('settingsDropdown.showNotifications')}
          </AccordionTrigger>
          <Button
            className="aspect-square h-10 w-auto bg-muted p-0 hover:border-border hover:bg-muted hover:text-foreground dark:bg-background dark:hover:bg-background"
            variant="outline"
            type="button"
            disabled={isDeleting}
            onClick={() => deleteChannel({ channelId })}
          >
            <Trash2 size={14} />
          </Button>
        </div>
      </div>
      {isCodeFormVisible && (
        <VerificationInput
          channelId={channelId}
          onHide={() => setIsCodeFormVisible(false)}
        />
      )}
      <AccordionContent className="flex grid-cols-2 flex-col pt-4 sm:grid">
        {notificationTypes.map((notificationType) => (
          <NotificationTypeRef
            channelType={type}
            notificationType={notificationType}
            preferences={preferences}
            key={notificationType}
          />
        ))}
      </AccordionContent>
    </AccordionItem>
  );
};

interface VerificationInputProps
  extends Pick<NotificationChannel, 'channelId'> {
  onHide(): void;
}

const VerificationInput = ({ channelId, onHide }: VerificationInputProps) => {
  const [attempt, setAttempt] = useState(1);
  const [codeInput, setCodeInput] = useState('');
  const [isInvalid, setIsInvalid] = useState(false);

  const { mutate: verifyChannel, isPending: isVerifying } = useVerifyChannel({
    onError: (err) => {
      if (err !== ErrorCode.INVALID_PARAMETER) return;
      setIsInvalid(true);
      if (attempt < 3) {
        toast.error(
          `Incorrect code. ${3 - attempt} attempt${3 - attempt === 1 ? '' : 's'} remaining.`,
        );
        setAttempt((prev) => prev + 1);
      } else {
        toast.error('Too many incorrect attempts. Please request a new code.');
        onHide();
      }
    },
  });

  const onCodeInputChange = (value: string) => {
    setCodeInput(value);
    setIsInvalid(false);
    if (value.length === 6) {
      verifyChannel({ channelId, code: value });
    }
  };

  return (
    <div className="flex flex-col items-center justify-center gap-1 pt-4">
      <span className="text-center font-medium capitalize">
        Check your mailbox
      </span>
      <span className="text-center text-sm text-muted-foreground">
        We&apos;ve sent you an email with verification code
      </span>
      <InputOTP
        disabled={isVerifying}
        value={codeInput}
        onChange={(value) => onCodeInputChange(value)}
        maxLength={6}
        containerClassName="mt-3"
        onPaste={(e) => {
          e.preventDefault();
          let pastedText = '';
          if (e.clipboardData && e.clipboardData.getData) {
            pastedText = e.clipboardData.getData('text/plain');
            // @ts-expect-error -- clipboardData not typed in Event
          } else if (window.clipboardData && window.clipboardData.getData) {
            // IE
            // @ts-expect-error -- clipboardData not typed in Event
            pastedText = window.clipboardData.getData('Text');
          }
          pastedText = pastedText.split(' ').join('');
          onCodeInputChange(pastedText);
        }}
      >
        <InputOTPGroup>
          {Array.from({ length: 3 }).map((_, i) => (
            <InputOTPSlot
              index={i}
              key={i}
              className={cn(
                'bg-muted dark:bg-background',
                isInvalid && 'border-red',
              )}
            />
          ))}
        </InputOTPGroup>
        <InputOTPGroup>
          {Array.from({ length: 3 }).map((_, i) => (
            <InputOTPSlot
              index={i + 3}
              key={i + 3}
              className={cn(
                'bg-muted dark:bg-background',
                isInvalid && 'border-red',
              )}
            />
          ))}
        </InputOTPGroup>
      </InputOTP>
    </div>
  );
};

type NotificationTypeProps = {
  channelType: NotificationChannelType;
  notificationType: string;
  preferences: NotificationChannel['preferences'];
};

const NotificationTypeRef = ({
  notificationType,
  channelType,
  preferences,
}: NotificationTypeProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();
  const isEnabled =
    preferences.find(
      (notification) => notification.notificationType === notificationType,
    )?.isEnabled ?? false;

  const { mutateAsync } = useToggleNotifications();

  const onChange = async (checked: boolean) => {
    setIsLoading(true);
    try {
      await mutateAsync({
        channelType,
        notificationType,
        enable: checked,
      });
    } catch (err) {
      /* empty */
    } finally {
      setIsLoading(false);
    }
  };

  const isMandatory = MANDATORY_NOTIFICATION_TYPES.includes(notificationType);

  return (
    <div className="flex items-center justify-between border-border py-2 sm:odd:pr-6 sm:even:border-l sm:even:pl-6">
      <span className="text-base">
        {t(`notifications.${notificationType}`)}
      </span>
      <Toggle
        disabled={(isEnabled && isMandatory) || isLoading}
        checked={isEnabled}
        onCheckedChange={onChange}
      />
    </div>
  );
};

type ChannelFormProps = {
  onHide(): void;
};

function ChannelForm({ onHide }: ChannelFormProps) {
  const { t } = useTranslation();
  const { email } = usePrivyAccount();

  const { data: notifications } = useNotifications();

  const defaultValues = useMemo<FormSchema>(() => {
    if (!notifications?.channels)
      return {
        type: NotificationChannelType.EMAIL,
        destination: '',
      };
    if (
      notifications.channels.some((channel) => channel.destination === email)
    ) {
      return {
        type: NotificationChannelType.EMAIL,
        destination: '',
      };
    }
    return {
      type: NotificationChannelType.EMAIL,
      destination: email ?? '',
    };
  }, [notifications?.channels, email]);

  const form = useForm<FormSchema>({
    defaultValues,
  });

  const { mutateAsync: createNotificationChannel } =
    useCreateNotificationChannel();

  async function onSubmit({ type, destination }: FormSchema) {
    await createNotificationChannel({
      channelType: type,
      destination,
    });
    onHide();
  }

  const type = form.watch('type');

  const { isSubmitting, isValid } = form.formState;

  return (
    <Form {...form}>
      <form className="flex gap-3" onSubmit={form.handleSubmit(onSubmit)}>
        <Select value={type} disabled>
          <SelectTrigger className="h-10 max-w-32 rounded-none bg-muted text-base disabled:opacity-75">
            <SelectValue />
          </SelectTrigger>
          <SelectContent align="end">
            <SelectItem className="text-base" value="email">
              {t('settingsDropdown.email')}
            </SelectItem>
          </SelectContent>
        </Select>
        <FormField
          control={form.control}
          name="destination"
          render={({ field }) => (
            <FormControl>
              <Input
                {...field}
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus
                className="border border-border bg-muted hover:bg-muted focus:bg-muted disabled:opacity-75 disabled:hover:bg-muted"
                placeholder="example@mail.com"
              />
            </FormControl>
          )}
        />
        <Button
          className="aspect-square h-10 w-auto rounded-none bg-muted p-0 hover:border-border hover:bg-muted hover:text-foreground"
          variant="outline"
          type="button"
          onClick={onHide}
        >
          <Trash2 size={14} />
        </Button>
        <Button
          disabled={isSubmitting || !isValid}
          className="h-10 rounded-none py-0"
        >
          <Check size={14} />
          {t('settingsDropdown.save')}
        </Button>
      </form>
    </Form>
  );
}
